import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Menu, MenuItem } from '@sgcmpteltd/uikit-react';
import {
  Result,
  RESULT_VARIANT_ERROR,
  RESULT_VARIANT_SUCCESS,
} from '@sgcmpteltd/uikit-react/components/result';
import cx from 'clsx';
import Cookies from 'js-cookie';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { ACTION_STATUS_ERROR, ACTION_STATUS_SUCCESS } from '@/actions';
import {
  reportError,
} from '@/actions/misc/home/report-error/new-car-promotion/post';
import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
} from '@/apis/server/new-car/v1/my-alert';
import type {
  ReportErrorResult,
} from '@/app/(routes)/(misc)/pricelists/_components/page-content/pi-pricelist-content/types'; // @suppress longLineCheck
import {
  Step as ReportErrorContent,
} from '@/app/(routes)/(misc)/pricelists/_components/page-content/report-error';
import type {
  AdAlertModalConent,
  CarAlertModalConent,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import {
  AlertModal,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import {
  NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/constants'; // @suppress longLineCheck
import type { User } from '@/app/types';
import { BrowsingHistoryModal } from '@/components/browsing-history';
import {
  COOKIE_KEY_APP_LOAD_FROM,
  COOKIE_KEY_MAIL,
  COOKIE_KEY_NON_LOGIN_SHORTLIST,
  COOKIE_KEY_SESSION_ID,
  COOKIE_KEY_USER_ID,
} from '@/configs/cookies';
import { SGCM_SITE_DOMAIN } from '@/configs/public-url';
import { SITE_BASE_PATH } from '@/configs/site';
import { isMobileAppPlatform, MOBILE_APP_PLATFORM_ANDROID } from '@/utils/app';
import {
  joinSgcmMedia,
  joinSgcmMsSite,
  joinSgcmSite,
} from '@/utils/url/join/public';

import { moreMenuItemsConfig, shareMenuItemsConfig } from './configs';
import {
  MORE_IMAGE_HEIGHT,
  MORE_IMAGE_WEIGHT,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
  PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
  PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
  SHARE_IMAGE_HEIGHT,
  SHARE_IMAGE_WEIGHT,
  SHORTLIST_IMAGE_WEIGHT,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const TitleAndQuickButton: Component = ({
  carDetail,
  title,
  subTitle,
  isShortlistOnClick,
  alertList,
  isPi,
  OnShortlistClick,
  onAlertModalSubmit,
}) => {
  const isBrowser = typeof window !== 'undefined';
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentUrlInBase64, setCurrentUrlInBase64] = useState('');
  const currentPathName = usePathname();
  const currentTab = currentPathName.split('/').pop();
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const [shareValue, setShareValue] = useState<string | number>();
  const [isReportPopupOpen, setIsReportPopupOpen] = useState(false);
  const [isBrowsingHistoryOpen, setIsBrowsingHistoryOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [
    isAlertModalOpen,
    setIsAlertModalOpen,
  ] = useState(false);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [isShortlisted, setIsShortlisted] = useState(isShortlistOnClick);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [
    reportErrorResultData, setReportErrorResultData,
  ] = useState<ReportErrorResult | null>(null);
  const [
    reportErrorStep, setReportErrorStep,
  ] = useState<'reportError' | 'errorSubmitResult'>('reportError');
  const { adAlert, carAlert } = alertList;
  const user: User = {
    email: Cookies.get(COOKIE_KEY_MAIL) || '',
    userId: Number(Cookies.get(COOKIE_KEY_USER_ID)) || 0,
    sessionId: Cookies.get(COOKIE_KEY_SESSION_ID) || '',
  };

  const showPrintableVersion = currentTab === 'specs'
    || currentTab === 'features';

  const carAlertModalContent: CarAlertModalConent = {
    carInfo: {
      id: carDetail.id,
      make: carDetail.make,
      model: carDetail.model,
    },
    carAlert,
  };
  const adAlertModalContent: AdAlertModalConent = {
    adInfo: {
      id: carDetail.authorizeDealers
        ? carDetail.authorizeDealers[0].id
        : 0,
      name: carDetail.authorizeDealers
        ? carDetail.authorizeDealers[0].name
        : '',
      brand: carDetail.make,
    },
    adAlert,
  };

  useEffect(() => {
    if (isBrowser) {
      const url = window.location.href;

      setCurrentUrl(url);
      const utf8Bytes = new TextEncoder().encode(url);

      setCurrentUrlInBase64(Buffer.from(utf8Bytes).toString('base64'));
    }
  }, [isBrowser]);

  useEffect(() => {
    setIsShortlisted((user.userId
      ? (carAlert.adCarPriceChange || carAlert.piCarPromo)
      : Cookies.get(COOKIE_KEY_NON_LOGIN_SHORTLIST)?.split(',')
        .includes(carDetail.id.toString()) ?? false));
  }, [carAlert, carDetail.id, user.userId]);

  const handleOnShortlistClick = () => {
    const shortlist = Cookies.get(COOKIE_KEY_NON_LOGIN_SHORTLIST);

    if (user.userId) {
      OnShortlistClick?.(!isShortlistOnClick);
      setIsAlertModalOpen(true);
    } else if (isDesktopMode) {
      window.open(
        joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
        '_self',
        'noreferrer noopener',
      );
    } else if (isShortlisted) {
      setIsShortlisted(false);
      Cookies.set(
        COOKIE_KEY_NON_LOGIN_SHORTLIST,
        shortlist
          ? shortlist.split(',')
            .filter(shortlistedId => shortlistedId !== carDetail.id.toString())
            .join(',')
          : '',
        {
          domain: process.env.NODE_ENV !== 'development'
            ? SGCM_SITE_DOMAIN
            : undefined,
        },
      );
    } else if (shortlist) {
      if (shortlist.split(',').length >= NUM_NON_LOGIN_SHORTLIST_MAX_VEHICLES) {
        setIsWarningModalOpen(true);
      } else {
        Cookies.set(
          COOKIE_KEY_NON_LOGIN_SHORTLIST,
          `${shortlist},${carDetail.id}`,
          {
            domain: process.env.NODE_ENV !== 'development'
              ? SGCM_SITE_DOMAIN
              : undefined,
          },
        );
        setIsShortlisted(true);
      }
    } else {
      Cookies.set(
        COOKIE_KEY_NON_LOGIN_SHORTLIST,
        carDetail.id.toString(),
        {
          domain: process.env.NODE_ENV !== 'development'
            ? SGCM_SITE_DOMAIN
            : undefined,
        },
      );
      setIsShortlisted(true);
    }
  };

  const handleOnShareBtnClick = () => {
    setIsShareMenuOpen(!isShareMenuOpen);
  };

  const handleOnMoreBtnClick = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  const handleAlertModalSubmit = (
    alerts: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    onAlertModalSubmit?.(alerts);
  };

  const handleMenuItemClick = (v?: string | number) => {
    setShareValue(v);
    setIsShareMenuOpen(false);

    const whatsappBaseUrl = 'https://api.whatsapp.com/send?text=';

    if (v === 'copy_link') {
      navigator.clipboard.writeText(currentUrl);
    }

    if (v === 'whatsapp_share_link') {
      const whatsappShareUrl = `${whatsappBaseUrl}${encodeURIComponent(
        currentUrl,
      )}`;

      window.open(whatsappShareUrl, '_blank');
    }

    if (v === 'facebook_share_link') {
      const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        currentUrl,
      )}`;

      window.open(facebookShareUrl, '_blank', 'noreferrer noopener');
    }

    if (v === 'popup') {
      setIsReportPopupOpen(true);
    }

    if (v === 'submit_a_review') {
      if (user.userId) {
        window.open(
          `${joinSgcmMsSite(SITE_BASE_PATH ?? '')}${`/info/${carDetail.id}/${carDetail.slug}/submit-review`}`,
          '_blank',
          'noreferrer noopener',
        );
      } else {
        window.open(
          joinSgcmSite(`/member/login?referrer=${currentUrlInBase64}`).toString(),
          '_self',
          'noreferrer noopener',
        );
      }
    }

    if (v === 'browsing_history') {
      setIsBrowsingHistoryOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        shareMenuRef.current
        && !shareMenuRef.current.contains(event.target as Node)
      ) {
        setIsShareMenuOpen(false);
      }

      if (
        moreMenuRef.current
        && !moreMenuRef.current.contains(event.target as Node)
      ) {
        setIsMoreMenuOpen(false);
      }
    };

    if (isShareMenuOpen || isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isShareMenuOpen, isMoreMenuOpen]);

  const handleOnMobileShareClick = async () => {
    const shareData = {
      title: document.title, // This will use the current page's title
      text: 'Check out this page!', // You can customize this text
      url: window.location.href, // This will use the current page's URL
    };
    const loadFrom = Cookies.get(COOKIE_KEY_APP_LOAD_FROM);

    if (loadFrom
      && isMobileAppPlatform(loadFrom)
      && loadFrom === MOBILE_APP_PLATFORM_ANDROID
      && window.Android) {
      // Use the Android native share method
      window.Android.shareLink(window.location.href);
    } else if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error(`Something went wrong: ${err}`);
      }
    } else {
      alert(`Web Share not supported in this browser. You can copy this link: ${shareData.url}`);
    }
  };

  return (
    <div className={styles.containerRoot}>
      <Modal
        className={cx(
          styles.containerModal,
          styles.isSmall,
        )}
        isOpen={isWarningModalOpen}
        title={null}
        onClose={() => setIsWarningModalOpen(false)} />
      <h1 className={styles.containerTitle}>
        {title}
        {' '}
        <span className={styles.subTitleText}>
          {subTitle}
        </span>
      </h1>
      <div className={styles.containerMobileQuickButton}>
        <button
          className={styles.quickBtn}
          onClick={handleOnMobileShareClick}>
          <Image
            alt="share icon"
            className={styles.iconShare}
            height={36}
            src={`${joinSgcmMedia('/public/new-cars/icon/iconShare.svg')}`}
            width={36} />
        </button>
        <button
          className={styles.quickBtn}
          onClick={handleOnShortlistClick}>
          <Image
            alt="shortlist icon"
            className={styles.iconShortlist}
            height={SHORTLIST_IMAGE_WEIGHT}
            src={
              isShortlisted ? `${joinSgcmMedia('/public/new-cars/icon/HeartContained.svg')}` : `${joinSgcmMedia('/public/new-cars/icon/HeartOutlined.svg')}`
            }
            width={SHORTLIST_IMAGE_WEIGHT} />
        </button>
      </div>
      <div className={styles.containerQuickButton}>
        <button
          className={cx(
            styles.quickBtn,
          )}
          id="shortlistIcon"
          onClick={handleOnShortlistClick}>
          <Image
            alt="shortlist icon"
            height={18}
            src={
              isShortlisted ? `${joinSgcmMedia('/public/new-cars/icon/HeartContained.svg')}` : `${joinSgcmMedia('/public/new-cars/icon/HeartOutlined.svg')}`
            }
            width={18} />
          <span>Shortlist</span>
        </button>
        {showPrintableVersion && carDetail.subModels.length > 0 && (
          <a
            href={`${joinSgcmSite(`/new_cars/newcars_print.php?CarCode=${carDetail.id}&Subcode=${carDetail.subModels[0].id}`)}`}
            rel="noreferrer"
            target="_blank">
            <button
              className={styles.quickBtn}>
              <Image
                alt="share icon"
                className={styles.iconShare}
                height={20}
                src={`${joinSgcmMedia('/public/new-cars/icon/iconPrinter.svg')}`}
                width={20} />
              <span>Printable Version</span>
            </button>

          </a>
        )}
        <div
          ref={shareMenuRef}
          className={cx(
            styles.containerShareBtn,
          )}>
          {isShareMenuOpen
            ? (
              <div className={styles.menu}>
                <Menu>
                  {shareMenuItemsConfig.map(v => (
                    <MenuItem
                      key={v.value}
                      className={styles.liDesktopMenuItem}
                      isActive={shareValue === v.value}
                      value={v.value}
                      onClick={handleMenuItemClick}>
                      {v.text}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )
            : null}
          <button
            className={cx(
              styles.quickBtn,
            )}
            onClick={() => handleOnShareBtnClick()}>
            <Image
              alt="share icon"
              height={SHARE_IMAGE_WEIGHT}
              src={`${joinSgcmMedia('/public/new-cars/icon/iconShare.svg')}`}
              width={SHARE_IMAGE_HEIGHT} />
            <span>Share</span>
          </button>
        </div>
        <div
          ref={moreMenuRef}
          className={styles.containerMoreBtn}>
          {isMoreMenuOpen
            ? (
              <div className={styles.menu}>
                <Menu>
                  {moreMenuItemsConfig.map(v => (
                    <MenuItem
                      key={v.value}
                      className={styles.liDesktopMenuItem}
                      isActive={shareValue === v.value}
                      value={v.value}
                      onClick={handleMenuItemClick}>
                      {v.text}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )
            : null}
          <button
            className={cx(
              styles.quickBtn,
              styles.isLastBtn,
            )}
            onClick={() => handleOnMoreBtnClick()}>
            <Image
              alt="more icon"
              height={MORE_IMAGE_HEIGHT}
              src={`${joinSgcmMedia('/public/used-cars/info/more.svg')}`}
              width={MORE_IMAGE_WEIGHT} />
            <div>More</div>
          </button>
        </div>
      </div>
      <AlertModal
        adAlertModalContent={adAlertModalContent}
        carAlertModalContent={carAlertModalContent}
        isOpen={isAlertModalOpen}
        type={isPi ? 'piAlert' : 'carAlert'}
        user={user}
        onClose={() => {
          setIsAlertModalOpen(false);
        }}
        onSubmit={handleAlertModalSubmit} />
      <BrowsingHistoryModal
        isOpen={isBrowsingHistoryOpen}
        onClose={() => {
          setIsBrowsingHistoryOpen(false);
        }} />
      <Modal
        className={cx(
          styles.containerModal,
          styles.filterPopup,
          reportErrorStep === 'errorSubmitResult' && styles.isSubmmitted,
        )}
        contentClassName={cx(
          styles.modal,
          styles.hasNoPadding,
        )}
        isOpen={isReportPopupOpen}
        title={reportErrorStep === 'reportError' ? 'Report Error' : null}
        onClose={() => {
          setIsReportPopupOpen(false);
        }}>
        {reportErrorStep === 'reportError'
        && (
          <ReportErrorContent
            onSubmit={async ({
              email: errorReportEmail,
              description: errorReportDescription,
              name: errorReportSenderName,
            }) => {
              const formData = new FormData();

              formData.set('email', errorReportEmail);
              formData.set('description', errorReportDescription);
              formData.set('link', currentUrl.toString());
              formData.set('name', errorReportSenderName);

              const actionResult = await reportError(formData);

              switch (actionResult.status) {
                case ACTION_STATUS_SUCCESS:
                  setReportErrorResultData({
                    isSuccess: true,
                    title: PROCESS_RESULT_TITLE_REPORT_ERROR_SUCCESS,
                    message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_SUCCESS,
                  });

                  break;
                case ACTION_STATUS_ERROR:
                default:
                  setReportErrorResultData({
                    isSuccess: false,
                    title: PROCESS_RESULT_TITLE_REPORT_ERROR_FAIL,
                    message: PROCESS_RESULT_MESSAGE_REPORT_ERROR_FAIL,
                  });
                  break;
              }

              setReportErrorStep('errorSubmitResult');
            }} />
        )}
        {reportErrorStep === 'errorSubmitResult' && reportErrorResultData
        && (
          <Result
            message={reportErrorResultData.message}
            title={reportErrorResultData.title}
            variant={
              reportErrorResultData.isSuccess
                ? RESULT_VARIANT_SUCCESS
                : RESULT_VARIANT_ERROR
            } />
        )}
      </Modal>
    </div>
  );
};
